import { AuthService } from './authService';
import { Task, TaskComment, TaskStatus, BusinessUnit, TaskPriority } from '../types/task';

// Use environment variable with fallback to the correct URL from your SharePoint site
const SHAREPOINT_SITE_URL = process.env.REACT_APP_SHAREPOINT_SITE_URL || 'https://netorgft17318468.sharepoint.com/sites/TrueCareClinicVolunteers';

// Configuration to switch between Graph API and REST API
// Use Graph API for tasks since REST API has token scope issues
const USE_REST_API = process.env.REACT_APP_USE_REST_API === 'true' || false; // Default to Graph API

export class SharePointTaskService {
  private authService: AuthService;
  private initialized: boolean = false;
  private lastRequestTime: number = 0;
  private requestCount: number = 0;
  private readonly MAX_REQUESTS_PER_MINUTE = 60;

  constructor(authService: AuthService) {
    this.authService = authService;
  }

  // Initialize SharePoint connection
  async initialize(): Promise<void> {
    if (this.initialized) return;
    
    try {
      // Skip list creation - work with existing lists only
      console.log('ℹ️ SharePoint Task service initialized (using existing lists only)');
      this.initialized = true;
      console.log('✅ SharePoint Task service initialized successfully');
    } catch (error) {
      console.error('❌ Failed to initialize SharePoint Task service:', error);
      // Don't throw - allow the app to continue with mock data
    }
  }

  private async getGraphApiHeaders(): Promise<Headers> {
    const token = await this.authService.getAccessToken();
    if (!token) {
      throw new Error('No access token available. Please sign in first.');
    }

    return new Headers({
      'Authorization': `Bearer ${token}`,
      'Content-Type': 'application/json',
    });
  }

  async makeGraphRequest(endpoint: string, options: RequestInit = {}, allowNonIndexedQueries: boolean = false): Promise<any> {
    try {
      // Rate limiting - only if we're hitting limits
      const now = Date.now();
      if (this.requestCount >= this.MAX_REQUESTS_PER_MINUTE) {
        console.warn('⚠️ Rate limit approaching, waiting before next request...');
        await new Promise(resolve => setTimeout(resolve, 2000)); // Wait 2 seconds
        this.requestCount = 0;
      }
      
      this.lastRequestTime = now;
      this.requestCount++;
      
      const headers = await this.getGraphApiHeaders();
      
      // Add Prefer header for non-indexed queries if requested
      if (allowNonIndexedQueries) {
        headers.set('Prefer', 'HonorNonIndexedQueriesWarningMayFailRandomly');
      }
      
      const response = await fetch(`https://graph.microsoft.com/v1.0${endpoint}`, {
        ...options,
        headers,
      });

      if (!response.ok) {
        const errorText = await response.text();
        console.error('❌ Graph API request failed:', response.status, errorText);
        throw new Error(`Graph API request failed: ${response.status} ${errorText}`);
      }

      return await response.json();
    } catch (error) {
      console.error('❌ Error making Graph API request:', error);
      throw error;
    }
  }

  // Create Tasks list if it doesn't exist
  async createTasksList(): Promise<void> {
    try {
      await this.initialize();
      const siteId = await this.getSiteId();
      
      console.log('🔍 Checking for existing Tasks list...');
      
      // Check if Tasks list exists using Graph API
      try {
        const listsResponse = await this.makeGraphRequest(`/sites/${siteId}/lists?$filter=displayName eq 'Tasks'`);
        if (listsResponse.value && listsResponse.value.length > 0) {
          console.log('✅ Tasks list already exists');
          return;
        }
      } catch (error) {
        // List doesn't exist, continue to create it
        console.log('🔍 Tasks list not found, will attempt to create if needed');
      }
      
      // Try to create the Tasks list using Graph API
      try {
        console.log('🔍 Creating new Tasks list...');
        const listData = {
          displayName: 'Tasks',
          template: 'genericList',
          columns: [
            {
              name: 'Title',
              text: {}
            },
            {
              name: 'Description',
              text: {}
            },
            {
              name: 'AssignedTo',
              text: {}
            },
            {
              name: 'BusinessUnit',
              choice: {
                choices: Object.values(BusinessUnit)
              }
            },
            {
              name: 'Urgent',
              boolean: {}
            },
            {
              name: 'Status',
              choice: {
                choices: Object.values(TaskStatus)
              }
            },
            {
              name: 'Priority',
              choice: {
                choices: Object.values(TaskPriority)
              }
            },
            {
              name: 'DueDate',
              dateTime: {}
            },
            {
              name: 'FieldTags',
              text: {}
            }
          ]
        };

        const response = await this.makeGraphRequest(`/sites/${siteId}/lists`, {
          method: 'POST',
          body: JSON.stringify(listData)
        });
        console.log('✅ Tasks list created successfully:', response);
      } catch (createError: any) {
        console.warn('⚠️ Could not create Tasks list:', createError?.message || createError);
        console.log('ℹ️ Will work with existing lists only');
        // Don't throw - allow the app to continue if list already exists or creation fails
      }
    } catch (error: any) {
      console.error('❌ Error checking/creating Tasks list:', error);
      // Don't throw - allow the app to continue
    }
  }

  async createTaskCommentsList(): Promise<void> {
    try {
      await this.initialize();
      const siteId = await this.getSiteId();
      
      // Check if the TaskComments list already exists
      try {
        const listResponse = await this.makeGraphRequest(`/sites/${siteId}/lists/TaskComments`);
        console.log('✅ TaskComments list already exists:', listResponse);
        return;
      } catch (error: any) {
        if (error.message?.includes('404')) {
          console.log('📝 TaskComments list does not exist, creating it...');
          
          // Create the TaskComments list with proper fields
          const listData = {
            displayName: 'TaskComments',
            description: 'Comments for tasks',
            columns: [
              {
                name: 'Title',
                column: {
                  name: 'Title',
                  enforceUniqueValues: false,
                  indexed: false,
                  nameAsDisplayName: true,
                  required: true,
                  text: {
                    allowMultipleLines: false,
                    appendChangesToExistingText: false,
                    linesForEditing: 0,
                    maxLength: 255,
                    textType: 'plain'
                  }
                }
              },
              {
                name: 'Comment',
                column: {
                  name: 'Comment',
                  enforceUniqueValues: false,
                  indexed: false,
                  nameAsDisplayName: true,
                  required: true,
                  text: {
                    allowMultipleLines: true,
                    appendChangesToExistingText: false,
                    linesForEditing: 5,
                    maxLength: 2000,
                    textType: 'plain'
                  }
                }
              },
              {
                name: 'TaskID',
                column: {
                  name: 'TaskID',
                  enforceUniqueValues: false,
                  indexed: false,
                  nameAsDisplayName: true,
                  required: true,
                  text: {
                    allowMultipleLines: false,
                    appendChangesToExistingText: false,
                    linesForEditing: 0,
                    maxLength: 255,
                    textType: 'plain'
                  }
                }
              },
              {
                name: 'MentionedUsers',
                column: {
                  name: 'MentionedUsers',
                  enforceUniqueValues: false,
                  indexed: false,
                  nameAsDisplayName: true,
                  required: false,
                  text: {
                    allowMultipleLines: false,
                    appendChangesToExistingText: false,
                    linesForEditing: 0,
                    maxLength: 1000,
                    textType: 'plain'
                  }
                }
              }
            ]
          };
          
          const response = await this.makeGraphRequest(`/sites/${siteId}/lists`, {
            method: 'POST',
            body: JSON.stringify(listData)
          });
          
          console.log('✅ TaskComments list created successfully:', response);
        } else {
          console.warn('⚠️ Error checking TaskComments list:', error);
        }
        return;
      }
    } catch (error) {
      console.error('❌ Error checking TaskComments list:', error);
      // Don't throw - allow the app to continue
    }
  }

  // Get all tasks with filtering
  async getTasks(filter?: {
    status?: TaskStatus;
    businessUnit?: BusinessUnit;
    assignedTo?: string;
    urgent?: boolean;
  }): Promise<Task[]> {
    try {
      await this.initialize();
      
      console.log('🔍 Fetching tasks from SharePoint...');
      
      if (USE_REST_API) {
        // Try to get tasks from SharePoint using REST API
        try {
          const response = await this.getListItems('Tasks', '?$select=Id,Title,Description,AssignedTo,BusinessUnit,Urgent,Status,Priority,DueDate,FieldTags,Created,Author&$orderby=Created desc');
          
          console.log('✅ Successfully fetched tasks from SharePoint (REST):', response.results.length);
          
          const tasks = response.results.map((item: any) => {
            return {
              id: item.Id.toString(),
              title: item.Title || 'Untitled Task',
              description: item.Description || '',
              assignedTo: item.AssignedTo || 'Unassigned',
              businessUnit: item.BusinessUnit || BusinessUnit.SUPPORT_SERVICES,
              urgent: item.Urgent || false,
              status: item.Status || TaskStatus.OPEN,
              createdBy: item.Author?.Title || 'Unknown',
              created: item.Created || new Date().toISOString(),
              dueDate: item.DueDate || null,
              priority: item.Priority || TaskPriority.MEDIUM,
              tags: item.FieldTags ? item.FieldTags.split(',').map((tag: string) => tag.trim()) : []
            };
          });
          
          // Apply filters if provided
          if (filter) {
            console.log('🔍 Applying filters to SharePoint data:', filter);
            return tasks.filter((task: Task) => {
              if (filter.status && task.status !== filter.status) return false;
              if (filter.businessUnit && task.businessUnit !== filter.businessUnit) return false;
              // Case-insensitive name matching for assignedTo
              if (filter.assignedTo && task.assignedTo.toLowerCase() !== filter.assignedTo.toLowerCase()) return false;
              if (filter.urgent !== undefined && task.urgent !== filter.urgent) return false;
              return true;
            });
          }
          
          return tasks;
          
        } catch (sharePointError: any) {
          console.error('❌ SharePoint list not found or accessible:', sharePointError?.message || sharePointError);
          throw new Error(`Failed to fetch tasks from SharePoint: ${sharePointError?.message || 'Unknown error'}`);
        }
      } else {
        // Try to get tasks from SharePoint using Graph API
        try {
          const siteId = await this.getSiteId();
          const response = await this.makeGraphRequest(`/sites/${siteId}/lists/Tasks/items?$expand=fields&$orderby=createdDateTime desc`);
          
          console.log('✅ Successfully fetched tasks from SharePoint (Graph):', response.value.length);
          
          const tasks = response.value.map((item: any) => {
            // Handle AssignedTo field - could be a string or Person/Group field
            let assignedTo = 'Unassigned';
            if (item.fields.AssignedTo) {
              if (typeof item.fields.AssignedTo === 'string') {
                assignedTo = item.fields.AssignedTo;
              } else if (item.fields.AssignedTo?.Title) {
                assignedTo = item.fields.AssignedTo.Title;
              } else if (item.fields.AssignedTo?.displayName) {
                assignedTo = item.fields.AssignedTo.displayName;
              }
            }
            
            return {
              id: item.id,
              title: item.fields.Title || 'Untitled Task',
              description: item.fields.Description || '',
              assignedTo: assignedTo,
              businessUnit: item.fields.BusinessUnit || BusinessUnit.SUPPORT_SERVICES,
              urgent: item.fields.Urgent || false,
              status: item.fields.Status || TaskStatus.OPEN,
              createdBy: item.createdBy?.user?.displayName || item.fields.Author?.Title || 'Unknown',
              created: item.createdDateTime || item.fields.Created || new Date().toISOString(),
              dueDate: item.fields.DueDate || null,
              priority: item.fields.Priority || TaskPriority.MEDIUM,
              tags: item.fields.FieldTags ? item.fields.FieldTags.split(',').map((tag: string) => tag.trim()).filter((tag: string) => tag) : []
            };
          });
          
          // Apply filters if provided
          if (filter) {
            console.log('🔍 Applying filters to SharePoint data:', filter);
            return tasks.filter((task: Task) => {
              if (filter.status && task.status !== filter.status) return false;
              if (filter.businessUnit && task.businessUnit !== filter.businessUnit) return false;
              // Case-insensitive name matching for assignedTo
              if (filter.assignedTo && task.assignedTo.toLowerCase() !== filter.assignedTo.toLowerCase()) return false;
              if (filter.urgent !== undefined && task.urgent !== filter.urgent) return false;
              return true;
            });
          }
          
          return tasks;
          
        } catch (sharePointError: any) {
          console.error('❌ SharePoint list not found or accessible:', sharePointError?.message || sharePointError);
          throw new Error(`Failed to fetch tasks from SharePoint: ${sharePointError?.message || 'Unknown error'}`);
        }
      }
    } catch (error) {
      console.error('❌ Error fetching tasks:', error);
      throw error;
    }
  }

  private getMockTasks(): Task[] {
    return [
      {
        id: '1',
        title: 'Review Volunteer Applications',
        description: 'Review and process new volunteer applications for the clinic',
        assignedTo: 'Sarah Johnson',
        businessUnit: BusinessUnit.SUPPORT_SERVICES,
        urgent: true,
        status: TaskStatus.IN_PROGRESS,
        createdBy: 'John Smith',
        created: new Date().toISOString(),
        dueDate: new Date(Date.now() + 7 * 24 * 60 * 60 * 1000).toISOString(),
        priority: TaskPriority.HIGH,
        tags: ['volunteers', 'applications']
      },
      {
        id: '2',
        title: 'Update Patient Records',
        description: 'Update patient records with latest medical information',
        assignedTo: 'Mike Davis',
        businessUnit: BusinessUnit.CLINICAL_OPERATIONS,
        urgent: false,
        status: TaskStatus.OPEN,
        createdBy: 'Lisa Chen',
        created: new Date().toISOString(),
        dueDate: new Date(Date.now() + 3 * 24 * 60 * 60 * 1000).toISOString(),
        priority: TaskPriority.MEDIUM,
        tags: ['patients', 'records']
      }
    ];
  }

  private async getSiteId(): Promise<string> {
    try {
      // Extract the hostname and site path from the URL
      const url = new URL(SHAREPOINT_SITE_URL);
      const hostname = url.hostname;
      const sitePath = url.pathname.replace('/sites/', '');
      
      // Try the correct format: /sites/{hostname}:/sites/{site-name}
      const response = await this.makeGraphRequest(`/sites/${hostname}:/sites/${sitePath}`);
      return response.id;
    } catch (error) {
      console.log('Failed to get site by URL, trying alternative formats...');
      
      try {
        // Try with just the site name
        const url = new URL(SHAREPOINT_SITE_URL);
        const sitePath = url.pathname.replace('/sites/', '');
        const response = await this.makeGraphRequest(`/sites/${sitePath}`);
        return response.id;
      } catch (error2) {
        console.log('Failed to get site by site name, trying root site...');
        try {
          // Try the tenant root site
          const rootResponse = await this.makeGraphRequest('/sites/root');
          return rootResponse.id;
        } catch (rootError) {
          console.log('Failed to get root site, trying to list all sites...');
          // List all sites to find the correct one
          const sitesResponse = await this.makeGraphRequest('/sites?$select=id,name,webUrl,displayName');
          if (sitesResponse.value && sitesResponse.value.length > 0) {
            return sitesResponse.value[0].id;
          }
          throw new Error('No SharePoint sites found');
        }
      }
    }
  }

  // Create a new task
  async createTask(taskData: Omit<Task, 'id' | 'created' | 'createdBy'>): Promise<Task> {
    try {
      await this.initialize();
      
      // Ensure the Tasks list exists
      await this.createTasksList();
      
      console.log('🔍 Creating new task:', taskData);
      
      if (USE_REST_API) {
        // Create task using REST API
        try {
          const response = await this.addListItem('Tasks', {
            Title: taskData.title,
            Description: taskData.description,
            AssignedTo: taskData.assignedTo,
            BusinessUnit: taskData.businessUnit,
            Urgent: taskData.urgent,
            Status: taskData.status,
            Priority: taskData.priority,
            DueDate: taskData.dueDate,
            FieldTags: taskData.tags?.join(', ') || ''
          });
          
          console.log('✅ Task created successfully (REST):', response);
          
          return {
            ...taskData,
            id: response.Id.toString(),
            created: response.Created || new Date().toISOString(),
            createdBy: response.Author?.Title || 'Unknown'
          };
          
        } catch (error: any) {
          console.warn('⚠️ Failed to create task via REST API:', error?.message || error);
          throw error;
        }
      } else {
        // Create task using Graph API
        try {
          const siteId = await this.getSiteId();
          const response = await this.makeGraphRequest(`/sites/${siteId}/lists/Tasks/items`, {
            method: 'POST',
            body: JSON.stringify({
              fields: {
                Title: taskData.title,
                Description: taskData.description,
                AssignedTo: taskData.assignedTo,
                BusinessUnit: taskData.businessUnit,
                Urgent: taskData.urgent,
                Status: taskData.status,
                Priority: taskData.priority,
                DueDate: taskData.dueDate,
                FieldTags: taskData.tags?.join(', ') || ''
              }
            })
          });
          
          console.log('✅ Task created successfully (Graph):', response);
          
          return {
            ...taskData,
            id: response.id,
            created: response.createdDateTime || new Date().toISOString(),
            createdBy: response.createdBy?.user?.displayName || 'Unknown'
          };
          
        } catch (error: any) {
          console.warn('⚠️ Failed to create task via Graph API:', error?.message || error);
          throw error;
        }
      }
    } catch (error) {
      console.error('❌ Error creating task:', error);
      throw error;
    }
  }

  // Update an existing task
  async updateTask(taskId: string, updates: Partial<Task>): Promise<Task> {
    try {
      await this.initialize();
      
      console.log('🔍 Updating task:', taskId, 'with updates:', updates);
      
      if (USE_REST_API) {
        // Update task using REST API
        try {
          const response = await this.updateListItem('Tasks', parseInt(taskId), {
            Title: updates.title,
            Description: updates.description,
            AssignedTo: updates.assignedTo,
            BusinessUnit: updates.businessUnit,
            Urgent: updates.urgent,
            Status: updates.status,
            Priority: updates.priority,
            DueDate: updates.dueDate,
            FieldTags: updates.tags?.join(', ')
          });
          
          console.log('✅ Task updated successfully (REST):', response);
          
          // Get the updated task
          const tasks = await this.getTasks();
          const updatedTask = tasks.find(task => task.id === taskId);
          if (!updatedTask) {
            throw new Error('Updated task not found');
          }
          
          return updatedTask;
          
        } catch (error: any) {
          console.warn('⚠️ Failed to update task via REST API:', error?.message || error);
          throw error;
        }
      } else {
        // Update task using Graph API
        try {
          const siteId = await this.getSiteId();
          const response = await this.makeGraphRequest(`/sites/${siteId}/lists/Tasks/items/${taskId}`, {
            method: 'PATCH',
            body: JSON.stringify({
              fields: {
                Title: updates.title,
                Description: updates.description,
                AssignedTo: updates.assignedTo,
                BusinessUnit: updates.businessUnit,
                Urgent: updates.urgent,
                Status: updates.status,
                Priority: updates.priority,
                DueDate: updates.dueDate,
                FieldTags: updates.tags?.join(', ')
              }
            })
          });
          
          console.log('✅ Task updated successfully (Graph):', response);
          
          // Get the updated task
          const tasks = await this.getTasks();
          const updatedTask = tasks.find(task => task.id === taskId);
          if (!updatedTask) {
            throw new Error('Updated task not found');
          }
          
          return updatedTask;
          
        } catch (error: any) {
          console.warn('⚠️ Failed to update task via Graph API:', error?.message || error);
          throw error;
        }
      }
    } catch (error) {
      console.error('❌ Error updating task:', error);
      throw error;
    }
  }

  // Delete a task
  async deleteTask(taskId: string): Promise<void> {
    try {
      await this.initialize();
      
      console.log('🔍 Deleting task:', taskId);
      
      if (USE_REST_API) {
        // Delete task using REST API
        try {
          await this.deleteListItem('Tasks', parseInt(taskId));
          console.log('✅ Task deleted successfully (REST)');
        } catch (error: any) {
          console.warn('⚠️ Failed to delete task via REST API:', error?.message || error);
          throw error;
        }
      } else {
        // Delete task using Graph API
        try {
          const siteId = await this.getSiteId();
          await this.makeGraphRequest(`/sites/${siteId}/lists/Tasks/items/${taskId}`, {
            method: 'DELETE'
          });
          console.log('✅ Task deleted successfully (Graph)');
        } catch (error: any) {
          console.warn('⚠️ Failed to delete task via Graph API:', error?.message || error);
          throw error;
        }
      }
    } catch (error) {
      console.error('❌ Error deleting task:', error);
      throw error;
    }
  }

  // Add a comment to a task
  async addTaskComment(taskId: string, comment: string, mentioned: string[] = []): Promise<TaskComment> {
    try {
      await this.initialize();
      
      console.log('🔍 Adding comment for task ID:', taskId, 'Comment:', comment);
      
      // Ensure the TaskComments list exists
      await this.createTaskCommentsList();
      
      const siteId = await this.getSiteId();
      
      // Get the TaskComments list
      const listResponse = await this.makeGraphRequest(`/sites/${siteId}/lists/TaskComments`);
      
      console.log('📋 TaskComments list for adding:', listResponse);
      
      // Create the comment data
      const commentData: any = {
        fields: {
          Title: `Comment for Task ${taskId}`,
          Comment: comment,
          TaskID: taskId
        }
      };
      
      // Add mentioned users if provided - store as a text field
      if (mentioned.length > 0) {
        commentData.fields.MentionedUsers = mentioned.join(', ');
        console.log(`✅ Added mentioned users: ${mentioned.join(', ')}`);
      }
      
      console.log('🔒 Comment data to be sent:', JSON.stringify(commentData, null, 2));
      
      // Create the comment item
      const response = await this.makeGraphRequest(`/sites/${siteId}/lists/${listResponse.id}/items`, {
        method: 'POST',
        body: JSON.stringify(commentData)
      });
      
      console.log('✅ Comment added successfully:', response);
      
      // Return the created comment
      const createdComment: TaskComment = {
        id: response.id,
        taskId: response.fields?.TaskID?.toString() || taskId,
        comment: response.fields?.Comment || comment,
        mentioned: response.fields?.MentionedUsers 
          ? response.fields.MentionedUsers.split(',').map((user: string) => user.trim())
          : mentioned,
        createdBy: response.fields?.Author?.Title || response.createdBy?.user?.displayName || 'Unknown',
        created: response.fields?.Created || response.createdDateTime || new Date().toISOString(),
        authorDisplayName: response.fields?.Author?.Title || response.createdBy?.user?.displayName || 'Unknown',
        authorEmail: response.fields?.Author?.EMail || response.createdBy?.user?.email || ''
      };
      
      console.log('📝 Created comment object:', createdComment);
      return createdComment;
      
    } catch (error) {
      console.error('❌ Error adding task comment:', error);
      throw error;
    }
  }

  // Get comments for a task
  async getTaskComments(taskId: string): Promise<TaskComment[]> {
    try {
      await this.initialize();
      
      console.log('🔍 Getting comments for task ID:', taskId);
      
      // Ensure the TaskComments list exists
      await this.createTaskCommentsList();
      
      const siteId = await this.getSiteId();
      
      // Get the TaskComments list
      const listResponse = await this.makeGraphRequest(`/sites/${siteId}/lists/TaskComments`);
      
      console.log('📋 TaskComments list for getting comments:', listResponse);
      
      // Get comments for this specific task
      const commentsResponse = await this.makeGraphRequest(
        `/sites/${siteId}/lists/${listResponse.id}/items?$filter=fields/TaskID eq '${taskId}'&$expand=fields&$orderby=fields/Created desc`,
        {},
        true // Allow non-indexed queries
      );
      
      console.log('📝 Comments response:', commentsResponse);
      
      if (!commentsResponse.value || commentsResponse.value.length === 0) {
        console.log('📝 No comments found for task:', taskId);
        return [];
      }
      
      // Process the comments
      const comments: TaskComment[] = commentsResponse.value.map((item: any) => ({
        id: item.id,
        taskId: item.fields?.TaskID?.toString() || taskId,
        comment: item.fields?.Comment || 'No content',
        mentioned: item.fields?.MentionedUsers 
          ? item.fields.MentionedUsers.split(',').map((user: string) => user.trim())
          : [],
        createdBy: item.fields?.Author?.Title || item.createdBy?.user?.displayName || 'Unknown',
        created: item.fields?.Created || item.createdDateTime || new Date().toISOString(),
        authorDisplayName: item.fields?.Author?.Title || item.createdBy?.user?.displayName || 'Unknown',
        authorEmail: item.fields?.Author?.EMail || item.createdBy?.user?.email || ''
      }));
      
      console.log('📝 Processed comments:', comments);
      return comments;
      
    } catch (error) {
      console.error('❌ Error getting task comments:', error);
      return [];
    }
  }

  // Get users for assignment
  async getUsers(): Promise<{ id: string; name: string; email: string; role: string; businessUnit?: string }[]> {
    try {
      await this.initialize();
      console.log('🔍 Fetching real users from SharePoint True Care Clinic Volunteer List');
      
      const siteId = await this.getSiteId();
      
      // Get all lists to find the volunteer list
      const listsResponse = await this.makeGraphRequest(`/sites/${siteId}/lists?$select=id,displayName,name,webUrl`);
      
      const volunteerList = listsResponse.value.find((list: any) => 
        list.displayName === 'True Care Clinic Volunteer List' || 
        list.displayName === 'Volunteer List' ||
        list.displayName === 'Volunteers' ||
        (list.displayName.toLowerCase().includes('volunteer') && 
         !list.displayName.toLowerCase().includes('availability') &&
         !list.displayName.toLowerCase().includes('schedule'))
      );
      
      if (!volunteerList) {
        console.log('⚠️ Volunteer list not found, returning mock users');
        return this.getMockUsers();
      }
      
      console.log('🔍 Found volunteer list:', volunteerList.displayName);
      
      // Get volunteers from the list with proper field mapping
      const volunteersResponse = await this.makeGraphRequest(`/sites/${siteId}/lists/${volunteerList.id}/items?$expand=fields&$top=5000`);
      
      console.log('🔍 Found volunteers:', volunteersResponse.value.length);
      
      const users = volunteersResponse.value.map((volunteer: any) => {
        const fields = volunteer.fields || {};
        
        // Use the same comprehensive field mapping as getVolunteers() in sharePointService.ts
        return {
          id: volunteer.id,
          name: fields.Title || fields.Name || fields.FullName || 'Unknown',
          email: fields.Email || fields.EMail || fields.EmailAddress || '',
          role: fields.AssignedRole || fields.Role || fields.Position || 'Volunteer',
          businessUnit: fields.BusinessUnit || fields.Unit || fields.Department || ''
        };
      });
      
      console.log('🔍 Transformed users:', users.length, 'users loaded');
      return users;
      
    } catch (error) {
      console.error('❌ Error fetching users:', error);
      return this.getMockUsers();
    }
  }

  private getMockUsers(): { id: string; name: string; email: string; role: string; businessUnit?: string }[] {
    return [
      { id: '1', name: 'Sarah Johnson', email: 'sarah.johnson@example.com', role: 'Manager', businessUnit: 'Operations' },
      { id: '2', name: 'Mike Davis', email: 'mike.davis@example.com', role: 'Volunteer', businessUnit: 'Clinical' },
      { id: '3', name: 'Lisa Chen', email: 'lisa.chen@example.com', role: 'Coordinator', businessUnit: 'Administration' }
    ];
  }

  // Get dashboard statistics
  async getDashboardStats(userId: string, userRole: string, userName?: string): Promise<any> {
    try {
      await this.initialize();
      
      console.log('🔍 Getting dashboard stats from SharePoint data...');
      
      // Get tasks based on user role
      let tasksToAnalyze: Task[] = [];
      if (userRole === 'Executive') {
        // Executives see all tasks
        tasksToAnalyze = await this.getTasks();
      } else {
        // Other users see only their assigned tasks
        tasksToAnalyze = await this.getTasks({ assignedTo: userName || userId });
      }
      
      console.log(`📊 Found ${tasksToAnalyze.length} tasks for statistics calculation`);
      
      // Calculate statistics
      const stats = {
        totalTasks: tasksToAnalyze.length,
        openTasks: tasksToAnalyze.filter(task => task.status === TaskStatus.OPEN).length,
        inProgressTasks: tasksToAnalyze.filter(task => task.status === TaskStatus.IN_PROGRESS).length,
        completedTasks: tasksToAnalyze.filter(task => task.status === TaskStatus.COMPLETE).length,
        urgentTasks: tasksToAnalyze.filter(task => task.urgent).length,
        myTasks: tasksToAnalyze.length, // All tasks shown are "my tasks" for non-executives
        overdueTasks: tasksToAnalyze.filter(task => {
          if (!task.dueDate) return false;
          const dueDate = new Date(task.dueDate);
          const now = new Date();
          return dueDate < now && task.status !== TaskStatus.COMPLETE;
        }).length,
        assignedByMe: 0 // Will be calculated separately if needed
      };
      
      console.log('📊 Calculated dashboard stats:', stats);
      return stats;
      
    } catch (error) {
      console.error('❌ Error getting dashboard stats:', error);
      return {
        totalTasks: 0,
        openTasks: 0,
        inProgressTasks: 0,
        completedTasks: 0,
        urgentTasks: 0,
        myTasks: 0,
        overdueTasks: 0,
        assignedByMe: 0
      };
    }
  }

  // REST API methods
  private async getRestApiHeaders(): Promise<Headers> {
    const token = await this.authService.getAccessToken();
    if (!token) {
      throw new Error('No access token available. Please sign in first.');
    }

    return new Headers({
      'Authorization': `Bearer ${token}`,
      'Content-Type': 'application/json',
      'Accept': 'application/json;odata=verbose'
    });
  }

  async makeRestRequest(endpoint: string, options: RequestInit = {}): Promise<any> {
    try {
      // Rate limiting
      const now = Date.now();
      if (this.requestCount >= this.MAX_REQUESTS_PER_MINUTE) {
        console.warn('⚠️ Rate limit approaching, waiting before next request...');
        await new Promise(resolve => setTimeout(resolve, 2000));
        this.requestCount = 0;
      }
      
      this.lastRequestTime = now;
      this.requestCount++;
      
      const headers = await this.getRestApiHeaders();
      
      // Convert headers to object for fetch
      const headerObject: Record<string, string> = {};
      headers.forEach((value, key) => {
        headerObject[key] = value;
      });
      
      const response = await fetch(`${SHAREPOINT_SITE_URL}/_api${endpoint}`, {
        ...options,
        headers: {
          ...headerObject,
          ...options.headers
        }
      });

      if (!response.ok) {
        const errorText = await response.text();
        console.error('❌ REST API request failed:', response.status, errorText);
        throw new Error(`REST API request failed: ${response.status} ${errorText}`);
      }

      return await response.json();
    } catch (error) {
      console.error('❌ Error making REST API request:', error);
      throw error;
    }
  }

  async getSiteInfo(): Promise<any> {
    try {
      const response = await this.makeRestRequest('/web?$select=Id,Title,Url');
      return response.d;
    } catch (error) {
      console.error('❌ Error getting site info:', error);
      throw error;
    }
  }

  async getListByTitle(listTitle: string): Promise<any> {
    try {
      const response = await this.makeRestRequest(`/web/lists/getbytitle('${listTitle}')?$select=Id,Title,ItemCount`);
      return response.d;
    } catch (error) {
      console.error('❌ Error getting list by title:', error);
      throw error;
    }
  }

  async createList(listTitle: string, template: string = 'GenericList'): Promise<any> {
    try {
      const response = await this.makeRestRequest('/web/lists', {
        method: 'POST',
        body: JSON.stringify({
          __metadata: { type: 'SP.List' },
          Title: listTitle,
          BaseTemplate: 100,
          AllowContentTypes: true,
          ContentTypesEnabled: true
        })
      });
      return response.d;
    } catch (error) {
      console.error('❌ Error creating list:', error);
      throw error;
    }
  }

  async addFieldToList(listTitle: string, fieldSchema: any): Promise<any> {
    try {
      const response = await this.makeRestRequest(`/web/lists/getbytitle('${listTitle}')/fields`, {
        method: 'POST',
        body: JSON.stringify(fieldSchema)
      });
      return response.d;
    } catch (error) {
      console.error('❌ Error adding field to list:', error);
      throw error;
    }
  }

  async getListItems(listTitle: string, query: string = ''): Promise<any> {
    try {
      const endpoint = `/web/lists/getbytitle('${listTitle}')/items${query}`;
      const response = await this.makeRestRequest(endpoint);
      return response.d;
    } catch (error) {
      console.error('❌ Error getting list items:', error);
      throw error;
    }
  }

  async addListItem(listTitle: string, itemData: any): Promise<any> {
    try {
      const response = await this.makeRestRequest(`/web/lists/getbytitle('${listTitle}')/items`, {
        method: 'POST',
        body: JSON.stringify({
          __metadata: { type: `SP.Data.${listTitle.replace(/\s+/g, '')}ListItem` },
          ...itemData
        })
      });
      return response.d;
    } catch (error) {
      console.error(`❌ Error adding item to list '${listTitle}':`, error);
      throw error;
    }
  }

  async updateListItem(listTitle: string, itemId: number, itemData: any): Promise<any> {
    try {
      const response = await this.makeRestRequest(`/web/lists/getbytitle('${listTitle}')/items(${itemId})`, {
        method: 'POST',
        headers: {
          'X-HTTP-Method': 'MERGE',
          'If-Match': '*'
        },
        body: JSON.stringify({
          __metadata: { type: `SP.Data.${listTitle.replace(/\s+/g, '')}ListItem` },
          ...itemData
        })
      });
      return response.d;
    } catch (error) {
      console.error(`❌ Error updating item in list '${listTitle}':`, error);
      throw error;
    }
  }

  async deleteListItem(listTitle: string, itemId: number): Promise<void> {
    try {
      await this.makeRestRequest(`/web/lists/getbytitle('${listTitle}')/items(${itemId})`, {
        method: 'POST',
        headers: {
          'X-HTTP-Method': 'DELETE',
          'If-Match': '*'
        }
      });
    } catch (error) {
      console.error(`❌ Error deleting item from list '${listTitle}':`, error);
      throw error;
    }
  }
}

// Export singleton instance
let taskServiceInstance: SharePointTaskService | null = null;

export const getTaskService = (authService: AuthService): SharePointTaskService => {
  if (!taskServiceInstance) {
    taskServiceInstance = new SharePointTaskService(authService);
  }
  return taskServiceInstance;
};

// Helper function to setup SharePoint lists for tasks
export const setupSharePointForTasks = async (authService: AuthService): Promise<void> => {
  try {
    console.log('🔧 Setting up SharePoint for Task Management...');
    
    const taskService = getTaskService(authService);
    
    // Initialize the service
    console.log('🔧 Step 1: Initializing SharePoint service...');
    await taskService.initialize();
    
    // Create the necessary lists
    console.log('🔧 Step 2: Creating Tasks list...');
    await taskService.createTasksList();
    
    console.log('🔧 Step 3: Creating TaskComments list...');
    await taskService.createTaskCommentsList();
    
    console.log('✅ SharePoint setup completed successfully!');
    console.log('📋 Created/Verified Lists:');
    console.log('   - Tasks (for task management)');
    console.log('   - TaskComments (for task comments)');
    
  } catch (error: any) {
    console.error('❌ Error setting up SharePoint for tasks:', error);
    console.error('❌ Setup error details:', {
      message: error?.message || 'Unknown error',
      stack: error?.stack,
      step: 'setupSharePointForTasks'
    });
    
    // Provide a more user-friendly error message
    if (error?.message?.includes('SharePoint connection failed')) {
      throw new Error('Unable to connect to SharePoint. Please check your site URL and permissions.');
    } else if (error?.message?.includes('No access token available')) {
      throw new Error('Authentication failed. Please sign in again.');
    } else {
      throw new Error(`SharePoint setup failed: ${error?.message || 'Unknown error'}`);
    }
  }
};

// Helper function to test SharePoint connection
export const testSharePointConnection = async (authService: AuthService): Promise<boolean> => {
  try {
    console.log('🔍 Testing SharePoint connection...');
    
    const taskService = getTaskService(authService);
    await taskService.initialize();
    
    // For now, just check if we can initialize without errors
    // The actual connection test will happen when we try to access data
    console.log('✅ SharePoint service initialized successfully');
    console.log('   Site URL:', SHAREPOINT_SITE_URL);
    
    return true;
  } catch (error) {
    console.error('❌ SharePoint connection failed:', error);
    return false;
  }
};